<template>
  <div class="schedules">
    <div class="schedules__content">
      <div class="schedules__calendar">
        <VProgressLinear
          v-if="loading"
          indeterminate
          color="#9b5fb3"
          class="schedules__loading"
        />

        <div class="schedules__header">
          <button
            type="button"
            class="schedules__header-toggle"
            :aria-expanded="datePickerOpen"
            @click="toggleDatePicker"
          >
            <span>{{ headerLabel }}</span>
            <VIcon
              :icon="datePickerOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'"
              size="20"
            />
          </button>
        </div>

        <div
          class="schedules__date-picker"
          :class="{ 'schedules__date-picker--open': datePickerOpen }"
        >
          <div class="schedules__date-picker-inner">
            <ScheduleXCalendar :calendar-app="miniCalendarApp" />
          </div>
        </div>

        <ScheduleXCalendar :calendar-app="calendarApp">
          <template #timeGridEvent="{ calendarEvent }">
            <CustomEvent
              :calendar-event="calendarEvent"
              :px-per-minute="PX_PER_MINUTE"
            />
          </template>
          <template #monthGridEvent="{ calendarEvent }">
            <CustomEvent :calendar-event="calendarEvent" compact />
          </template>
          <template #monthAgendaEvent="{ calendarEvent }">
            <CustomEvent :calendar-event="calendarEvent" compact />
          </template>
        </ScheduleXCalendar>
      </div>
    </div>

    <VDialog v-model="sidebarOpen" width="420" scrollable>
      <VCard class="reservation-dialog">
        <VCardTitle class="reservation-dialog__header">
          <span class="reservation-dialog__title">{{
            $t("reservationing")
          }}</span>
          <VBtn
            icon="mdi-close"
            variant="text"
            size="small"
            :aria-label="$t('close')"
            @click="sidebarOpen = false"
          />
        </VCardTitle>
        <VCardText>
          <v-form ref="form" v-model="valid">
            <v-text-field
              v-model="editedReservation.start_date"
              :label="$t('start_date')"
              type="datetime-local"
              :rules="[requiredRule]"
              required
            ></v-text-field>
            <v-text-field
              v-model="editedReservation.end_date"
              :label="$t('end_date')"
              type="datetime-local"
              :rules="[requiredRule, endAfterStartRule]"
              required
            ></v-text-field>
            <v-select
              v-model="editedReservation.services"
              :items="globalStore.services"
              :label="$t('services')"
              item-title="name"
              item-value="id"
              multiple
            >
              <template #item="{ item, props: itemProps }">
                <VListItem
                  v-bind="itemProps"
                  :title="item.raw.name"
                  :subtitle="serviceDurationLabel(item.raw.id)"
                />
              </template>
            </v-select>
            <v-select
              v-model="editedReservation.employees"
              :items="globalStore.employees"
              :label="$t('employees')"
              item-title="name"
              item-value="id"
              multiple
            ></v-select>
            <v-combobox
              v-model="customerInput"
              v-model:menu="customerMenuOpen"
              :items="customerSearchResults"
              :loading="customerSearchLoading"
              :label="$t('customer')"
              :hint="$t('customer_search_hint')"
              persistent-hint
              item-title="name"
              return-object
              clearable
              hide-no-data
              no-filter
              @update:search="onCustomerSearch"
              @update:model-value="onCustomerSelect"
            >
              <template #item="{ item, props: itemProps }">
                <VListItem
                  v-bind="itemProps"
                  :title="customerDisplayName(item.raw)"
                  :subtitle="customerDisplaySubtitle(item.raw)"
                />
              </template>
              <template #selection="{ item }">
                {{ customerDisplayName(item.raw) }}
              </template>
            </v-combobox>
            <v-text-field
              v-model="editedReservation.customer.phone_number"
              :label="$t('phone_number')"
            ></v-text-field>
            <v-text-field
              v-model="editedReservation.comment"
              :label="$t('comments')"
            ></v-text-field>
            <VCol style="display: flex; justify-content: space-between">
              <VBtn variant="text" @click="sidebarOpen = false">{{
                $t("cancel")
              }}</VBtn>
              <VBtn
                color="#403838"
                variant="flat"
                :disabled="!valid"
                @click="saveReservation"
              >
                {{ $t("save") }}
              </VBtn>
            </VCol>
          </v-form>
        </VCardText>
      </VCard>
    </VDialog>

    <EventDialog
      v-model="eventDialogOpen"
      :reservation="selectedReservation"
      @edit="onEditFromDialog"
    />

    <VBtn
      v-if="!sidebarOpen"
      class="schedules__fab"
      icon="mdi-plus"
      size="large"
      color="primary"
      elevation="4"
      :aria-label="$t('new_reservation')"
      @click="openCreateSidebarFromFab"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from "vue";
import { ScheduleXCalendar } from "@schedule-x/vue";
import {
  createCalendar,
  createViewDay,
  createViewWeek,
  createViewMonthGrid,
  createViewMonthAgenda,
  CalendarEventExternal,
} from "@schedule-x/calendar";
import { createCalendarControlsPlugin } from "@schedule-x/calendar-controls";
import { createEventsServicePlugin } from "@schedule-x/events-service";
import "@schedule-x/theme-default/dist/index.css";
// Schedule-X's internals expect a global `Temporal` (per its own setup docs),
// while the typed import right below gives us type-safe access to the same
// polyfilled classes for building event dates ourselves.
import "temporal-polyfill/global";
import { Temporal } from "temporal-polyfill";
import {
  createReservation,
  getCompanyEmployees,
  getCompanyServices,
  getGlobalServices,
  getReservations,
  searchReservationCustomers,
  updateReservation,
} from "../services/api.service";
import { useCompanyStore } from "../stores/company.store";
import moment from "moment";
import {
  Reservation,
  ReservationCustomerInput,
  ReservationCustomerSearchResult,
  ReservationStatus,
} from "../types/reservation.type";
import CustomEvent from "../components/custom-event.vue";
import EventDialog from "../components/dialogs/event-dialog.vue";
import { useGlobalStore } from "../stores/global.store";
import { useI18n } from "vue-i18n";
import { useDisplay } from "vuetify";
import { RESERVATION_STATUS_COLORS } from "../helpers/reservation-status.helper";

const { locale, t } = useI18n();
// Not Vuetify's own `mobile` (tied to its `lg`/1280px breakpoint by
// default) - that would count tablets as mobile too. This matches the
// app's own $bp-tablet (see ../styles/_breakpoints.scss, used below via
// `bp.tablet`) so "phone" here means the same width everywhere else on
// this page, and tablet/desktop both land on week view.
const { width } = useDisplay();
const isPhoneWidth = computed(() => width.value <= 768);
const companyStore = useCompanyStore();
const globalStore = useGlobalStore();

// Schedule-X ships its own vocabulary as e.g. "uk-UA", not the bare "uk"
// this app's i18n uses.
const toScheduleXLocale = (appLocale: string) =>
  appLocale === "uk" ? "uk-UA" : "en-US";

const loading = ref(false);
const hideSidebar = ref(true);
const reservations = ref<Reservation[]>([]);
const eventDialogOpen = ref(false);
const selectedReservation = ref<Reservation | null>(null);
const editedReservation = ref<Reservation | any>({
  id: 0,
  start_date: "",
  end_date: "",
  status: ReservationStatus.Draft,
  comment: "",
  services: [],
  users: [],
  employees: [],
  customer: {} as ReservationCustomerInput,
  url: "",
});
const valid = ref(false);
const form = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(null);

// Only the time range is actually required - services, employees, and the
// customer are all optional, since a reservation can legitimately be
// created with none of them just to block that slot off from other
// bookings (see `applyServiceDurationToEndDate` and the empty-reservation
// support in `createEvent` below). `required` alone on a Vuetify field is
// purely cosmetic (just draws an asterisk) without an accompanying
// `:rules` array, so leaving it off those fields keeps the UI honest.
const requiredRule = (value: unknown) => !!value || t("field_required");

const endAfterStartRule = () => {
  if (
    !editedReservation.value.start_date ||
    !editedReservation.value.end_date
  ) {
    return true;
  }
  return (
    editedReservation.value.end_date > editedReservation.value.start_date ||
    t("end_after_start")
  );
};

const customerInput = ref<string | ReservationCustomerSearchResult | null>(
  null,
);
const customerSearchResults = ref<ReservationCustomerSearchResult[]>([]);
const customerSearchLoading = ref(false);
const customerMenuOpen = ref(false);
let customerSearchTimeout: ReturnType<typeof setTimeout> | undefined;

// Search results commonly have a blank `name` (walk-in customers/users who
// never filled one in) - falling back to phone/email keeps both the
// dropdown row and the field's own display of a made selection from going
// blank, which otherwise looked like the selection silently failed.
//
// Also has to handle a plain string: editing an existing reservation seeds
// `customerInput` with just the customer's name (see `editReservation`),
// not a full search-result object, and the `#selection` slot below calls
// this the same way either way - without this branch, `item.raw` would be
// that raw string and `.name`/`.phone_number`/`.email` would all be
// undefined, rendering the field blank even though a customer was set.
const customerDisplayName = (
  customer: ReservationCustomerSearchResult | string | null | undefined,
) => {
  if (!customer) return "";
  if (typeof customer === "string") return customer;
  return customer.name?.trim() || customer.phone_number || customer.email || "";
};

const customerDisplaySubtitle = (customer: ReservationCustomerSearchResult) => {
  const name = customer.name?.trim();
  return [customer.phone_number, customer.email]
    .filter((value) => value && value !== name)
    .join(" · ");
};

// Typing without picking a suggestion means "new walk-in customer" - keep
// their typed text as the name so it's still submitted even if they never
// select anything from the dropdown.
//
// Only touch `.name` when there's an actual query: because this field uses
// a custom `#selection` slot, Vuetify's VCombobox commits free-typed text
// on blur by internally calling its own `select()`, which sets the model
// (routing through `onCustomerSelect` below) *and then* resets its internal
// search text to "" - which re-fires this same `@update:search` handler
// with an empty string right after. Without this guard, that trailing
// empty-query call would immediately overwrite the name `onCustomerSelect`
// just set, silently dropping the typed customer on save.
const onCustomerSearch = (query: string) => {
  if (query) {
    editedReservation.value.customer = {
      ...editedReservation.value.customer,
      customer_id: undefined,
      name: query,
    };
  }

  clearTimeout(customerSearchTimeout);
  if (!query || query.length < 2) {
    customerSearchResults.value = [];
    customerMenuOpen.value = false;
    return;
  }

  customerSearchTimeout = setTimeout(async () => {
    customerSearchLoading.value = true;
    try {
      const results = await searchReservationCustomers(query);
      // The backend returns one row per past reservation, not per unique
      // customer/user - collapse those back down so someone who's booked
      // three times doesn't show up as three identical rows.
      const seen = new Set<string>();
      customerSearchResults.value = results.filter((result) => {
        const key = `${result.type}:${result.id}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      // `@update:search` fires before this async fetch resolves, so the
      // menu's own auto-open check already ran against the *previous*
      // (empty) results - open it explicitly once real matches land,
      // instead of leaving a search that found matches showing nothing.
      customerMenuOpen.value = customerSearchResults.value.length > 0;
    } catch (error) {
      console.error(error);
    } finally {
      customerSearchLoading.value = false;
    }
  }, 300);
};

// Picking an existing walk-in customer carries its id along so the backend
// reuses that record; picking an existing real user (or leaving the field as
// free text) carries no id - the backend matches an existing account by
// phone/email, or creates a new walk-in customer otherwise.
const onCustomerSelect = (
  value: string | ReservationCustomerSearchResult | null,
) => {
  if (value && typeof value === "object") {
    // `id` is always a Customer id now (whether or not it's linked to a
    // real account) - the backend attaches that record's own `user`, if
    // any, alongside it (see Reservations::AssignCustomerService).
    editedReservation.value.customer = {
      customer_id: value.id,
      name: value.name,
      phone_number: value.phone_number,
      email: value.email,
    };
  } else {
    editedReservation.value.customer = {
      ...editedReservation.value.customer,
      customer_id: undefined,
      name: value || "",
    };
  }
};

const sidebarOpen = computed({
  get: () => !hideSidebar.value,
  set: (open: boolean) => {
    hideSidebar.value = !open;
    if (!open) resetEditedReservation();
  },
});

// The device's current timezone, read once at module load. Following how
// this app stores everything in UTC and expects each surface to render it
// in whatever timezone is locally relevant - here, the browser's.
const localTimeZone = Temporal.Now.timeZoneId();

const toZonedDateTime = (isoString: string) =>
  Temporal.Instant.from(isoString).toZonedDateTimeISO(localTimeZone);

const reservationToEvent = (
  reservation: Reservation,
): CalendarEventExternal => ({
  id: reservation.id,
  title: reservation.services.map((service) => service.name).join(", "),
  start: toZonedDateTime(reservation.start_date),
  end: toZonedDateTime(reservation.end_date),
  calendarId: reservation.status,
  data: reservation,
});

// One `calendars` entry per reservation status, driving both the default
// event-chip colors and the month-agenda view's per-day color dots - reuses
// the exact same palette as our custom event components (see
// reservation-status.helper.ts) so both stay in sync automatically.
const calendars = Object.fromEntries(
  Object.entries(RESERVATION_STATUS_COLORS).map(([status, lightColors]) => [
    status,
    { colorName: status, lightColors },
  ]),
);

const dayView = createViewDay();
const weekView = createViewWeek();
const monthGridView = createViewMonthGrid();
const monthAgendaView = createViewMonthAgenda();

// No one works overnight, so hide those hours from the day/week grids.
// end must stay a whole hour later than start (an end of "00:00" would be
// read as a wrap-around "hybrid" day and show all 24 hours again).
const DAY_BOUNDARIES = { start: "07:00", end: "23:00" };
// Schedule-X's own default (1600px spread over the dayBoundaries range) is
// taller than this page's calendar panel on most screens, forcing the
// time grid itself to scroll - short enough to fit the whole day on one
// screen instead.
const GRID_HEIGHT = 700;
const DAY_BOUNDARY_HOURS =
  Number(DAY_BOUNDARIES.end.split(":")[0]) -
  Number(DAY_BOUNDARIES.start.split(":")[0]);
// How many px tall a one-minute event renders at - CustomEvent uses this
// to know ahead of layout whether its own box will be too short for its
// normal two-line (title + time) display.
const PX_PER_MINUTE = GRID_HEIGHT / (DAY_BOUNDARY_HOURS * 60);

const calendarControls = createCalendarControlsPlugin();
const eventsService = createEventsServicePlugin();

// Drives the centered "September 2026"-style header label - declared here
// (ahead of `calendarApp`) since its `onRangeUpdate` callback below writes
// to it. Week view's range can straddle two months; showing the range's
// first day's month matches what the mini calendar opens to (see
// toggleDatePicker further down).
const headerDate = ref(Temporal.Now.plainDateISO(localTimeZone));

const fetchEvents = async ({
  start,
  end,
}: {
  start: Temporal.ZonedDateTime;
  end: Temporal.ZonedDateTime;
}) => {
  if (!companyStore.currentCompany) return [];

  loading.value = true;
  try {
    const response = await getReservations(
      companyStore.currentCompany.id.toString(),
      start.toInstant().toString(),
      end.toInstant().toString(),
    );

    reservations.value = response;
    return response.map(reservationToEvent);
  } catch (error) {
    console.error(error);
    return [];
  } finally {
    loading.value = false;
  }
};

// The calendar app instance is not reactive - do not wrap it in a ref, and
// use the calendarControls/eventsService plugins for anything that needs to
// change after creation (locale, events, ...).
const calendarApp = createCalendar(
  {
    views: [dayView, weekView, monthGridView, monthAgendaView],
    defaultView: isPhoneWidth.value ? dayView.name : weekView.name,
    locale: toScheduleXLocale(locale.value),
    timezone: localTimeZone,
    firstDayOfWeek: 1,
    isDark: false,
    // No one works overnight, so hide those hours from the day/week grids.
    // end must stay a whole hour later than start (an end of "00:00" would
    // be read as a wrap-around "hybrid" day and show all 24 hours again).
    dayBoundaries: DAY_BOUNDARIES,
    // Schedule-X's own default (1600px spread over the dayBoundaries range)
    // is taller than this page's calendar panel on most screens, forcing
    // the time grid itself to scroll - short enough to fit the whole day
    // on one screen instead. CustomEvent uses the derived PX_PER_MINUTE
    // (below) to know when an event's own box is too short for its normal
    // two-line layout, so keep both in sync if this changes.
    weekOptions: {
      gridHeight: GRID_HEIGHT,
    },
    calendars,
    events: [],
    callbacks: {
      onEventClick(calendarEvent) {
        selectedReservation.value = (calendarEvent.data as Reservation) ?? null;
        eventDialogOpen.value = true;
      },
      onClickDateTime(dateTime) {
        openCreateSidebar(dateTime);
      },
      onRangeUpdate(range) {
        headerDate.value = range.start.toPlainDate();
      },
      async fetchEvents(range) {
        return await fetchEvents(range);
      },
    },
  },
  [calendarControls, eventsService],
);

// Reservations fetched for the mini month-picker calendar (see below) are
// only ever used to decide which days get a dot marker - unlike the main
// `fetchEvents`, this must not touch `reservations.value`/`loading.value`,
// which the rest of the page treats as "the currently displayed day/week's
// reservations" and "is the main grid loading".
const fetchDatePickerEvents = async ({
  start,
  end,
}: {
  start: Temporal.ZonedDateTime;
  end: Temporal.ZonedDateTime;
}) => {
  if (!companyStore.currentCompany) return [];

  try {
    const response = await getReservations(
      companyStore.currentCompany.id.toString(),
      start.toInstant().toString(),
      end.toInstant().toString(),
    );
    return response.map(reservationToEvent);
  } catch (error) {
    console.error(error);
    return [];
  }
};

const datePickerOpen = ref(false);
const miniCalendarControls = createCalendarControlsPlugin();
const miniEventsService = createEventsServicePlugin();

// A second, independent calendar app rather than switching the main one's
// view: the main grid (with its scroll position and fetched events) stays
// mounted and untouched underneath, while this one is a compact, header-
// less month grid rendered as a dropdown - see `miniMonthGridView` in
// `monthGridOptions`/CSS below for how its day cells are stripped down to
// plain dot markers instead of full event chips.
const pickDate = (date: Temporal.PlainDate) => {
  calendarControls.setDate(date);
  // Keeps the mini calendar's own "is-selected" highlight (see CSS below)
  // in sync immediately, rather than only on the next toggleDatePicker.
  miniCalendarControls.setDate(date);
};

const miniMonthGridView = createViewMonthGrid();
const miniCalendarApp = createCalendar(
  {
    views: [miniMonthGridView],
    defaultView: miniMonthGridView.name,
    locale: toScheduleXLocale(locale.value),
    timezone: localTimeZone,
    firstDayOfWeek: 1,
    isDark: false,
    monthGridOptions: {
      nEventsPerDay: 0,
    },
    calendars,
    events: [],
    callbacks: {
      onClickDate(date) {
        pickDate(date);
      },
      onClickPlusEvents(date) {
        pickDate(date);
      },
      async fetchEvents(range) {
        return await fetchDatePickerEvents(range);
      },
    },
  },
  [miniCalendarControls, miniEventsService],
);

const toggleDatePicker = () => {
  if (!datePickerOpen.value) {
    miniCalendarControls.setDate(calendarControls.getDate());
  }
  datePickerOpen.value = !datePickerOpen.value;
};

const headerLabel = computed(() => {
  const jsDate = new Date(
    headerDate.value.toZonedDateTime(localTimeZone).epochMilliseconds,
  );
  const formatted = new Intl.DateTimeFormat(toScheduleXLocale(locale.value), {
    month: "long",
    year: "numeric",
  }).format(jsDate);

  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
});

watch(locale, (newLocale) => {
  calendarControls.setLocale(toScheduleXLocale(newLocale));
  miniCalendarControls.setLocale(toScheduleXLocale(newLocale));
});

// Crossing the phone/tablet+desktop boundary after mount (resizing the
// window, rotating a tablet) switches the view too, not just its initial
// pick above.
watch(isPhoneWidth, (isPhone) => {
  calendarControls.setView(isPhone ? dayView.name : weekView.name);
});

onMounted(async () => {
  try {
    const companyId = companyStore.currentCompany?.id || 0;
    const [services, employees, companyServices] = await Promise.all([
      getGlobalServices([]),
      getCompanyEmployees(companyId),
      getCompanyServices(companyId),
    ]);
    globalStore.services = services;
    globalStore.employees = employees;
    companyStore.services = companyServices;
  } catch (error) {
    console.error(error);
  }
});

// The services select lists the global service catalog (name only), but
// this company's own duration for each is only known via its configured
// CompanyService rows (joined by service_id) - shown as a subtitle so
// picking a service also shows how long the slot will take.
const serviceDurationLabel = (globalServiceId: number) => {
  const companyService = companyStore.services.find(
    (s) => s.service_id === globalServiceId,
  );
  if (!companyService) return undefined;
  return t("booking.duration_minutes", { n: companyService.duration });
};

const totalServiceDuration = (serviceIds: number[]) =>
  serviceIds.reduce((total, id) => {
    const companyService = companyStore.services.find(
      (s) => s.service_id === id,
    );
    return total + (companyService?.duration ?? 0);
  }, 0);

// Keeps end_date in sync with the combined duration of the selected
// services, so the user doesn't have to compute/enter it by hand - it
// re-runs whenever either the service selection or the start time changes,
// and is a no-op once no service has a known duration (e.g. none selected)
// so a manually-entered end_date is left alone in that case.
const applyServiceDurationToEndDate = () => {
  if (!editedReservation.value.start_date) return;
  const duration = totalServiceDuration(editedReservation.value.services || []);
  if (duration <= 0) return;
  editedReservation.value.end_date = moment(editedReservation.value.start_date)
    .add(duration, "minutes")
    .format("YYYY-MM-DDTHH:mm");
};

// Mirrors the backend's own fallback (see Company#sole_employee) - a
// single-employee business has only one possible answer to "who's this
// for", so pre-select them here too instead of leaving the field blank and
// making the owner pick their own name every time. Only for a brand-new
// reservation (id === 0) with nothing chosen yet, so it never overwrites an
// explicit selection or something being edited.
const applySoleEmployeeDefault = () => {
  if (
    editedReservation.value.id === 0 &&
    editedReservation.value.employees.length === 0 &&
    globalStore.employees.length === 1
  ) {
    editedReservation.value.employees = [globalStore.employees[0].id];
  }
};

// Employees load asynchronously (see onMounted below) - if the create
// dialog is opened before that resolves, re-apply the default once the
// list actually arrives instead of leaving the field blank forever.
watch(() => globalStore.employees, applySoleEmployeeDefault);

watch(
  () => editedReservation.value.services,
  applyServiceDurationToEndDate,
  { deep: true },
);
watch(() => editedReservation.value.start_date, applyServiceDurationToEndDate);

// `eventsService.add`/`.update` only patch the in-memory event list - they
// don't move the visible date range, so a reservation saved for a day
// outside whatever the calendar currently shows would otherwise not
// actually appear anywhere on screen until the user manually navigated
// there (looking exactly like the save silently failed). Jumping the
// calendar (and the mini date-picker) to the saved reservation's own date
// guarantees it's visible right away.
const revealReservationDate = (reservation: Reservation) => {
  const date = toZonedDateTime(reservation.start_date).toPlainDate();
  calendarControls.setDate(date);
  miniCalendarControls.setDate(date);
};

const saveReservation = async () => {
  // Only the time range has real `:rules` (see above) - still validate the
  // whole form before submitting so a bad/missing start or end date can't
  // slip through.
  const result = await form.value?.validate();
  if (result && !result.valid) return;

  if (editedReservation.value.id === 0) {
    await createEvent();
  } else {
    await updateEvent();
  }

  sidebarOpen.value = false;
};

const createEvent = async () => {
  try {
    const newReservation = await createReservation({
      employee_ids: editedReservation.value.employees as number[],
      service_ids: editedReservation.value.services as number[],
      start_date: moment(editedReservation.value.start_date).toISOString(),
      end_date: moment(editedReservation.value.end_date).toISOString(),
      customer: editedReservation.value.customer?.name
        ? editedReservation.value.customer
        : undefined,
    });

    resetEditedReservation();

    reservations.value.push(newReservation);
    eventsService.add(reservationToEvent(newReservation));
    miniEventsService.add(reservationToEvent(newReservation));
    revealReservationDate(newReservation);
  } catch (error) {
    console.error(error);
  }
};

const updateEvent = async () => {
  try {
    const updatedReservation = await updateReservation({
      id: editedReservation.value.id,
      employee_ids: editedReservation.value.employees as number[],
      service_ids: editedReservation.value.services as number[],
      start_date: moment(editedReservation.value.start_date).toISOString(),
      end_date: moment(editedReservation.value.end_date).toISOString(),
      customer: editedReservation.value.customer?.name
        ? editedReservation.value.customer
        : undefined,
    });

    reservations.value = reservations.value.map((reservation) =>
      reservation.id === updatedReservation.id
        ? updatedReservation
        : reservation,
    );

    eventsService.update(reservationToEvent(updatedReservation));
    miniEventsService.update(reservationToEvent(updatedReservation));
    revealReservationDate(updatedReservation);
    resetEditedReservation();
  } catch (error) {
    console.error(error);
  }
};

const editReservation = (reservation: Reservation) => {
  sidebarOpen.value = true;
  const existingCustomer = reservation.customers?.[0];
  const customer: ReservationCustomerInput = existingCustomer
    ? {
        customer_id: existingCustomer.id,
        name: existingCustomer.name,
        phone_number: existingCustomer.phone_number,
        email: existingCustomer.email,
      }
    : {};

  editedReservation.value = {
    ...reservation,
    start_date: moment(reservation.start_date).format("YYYY-MM-DDTHH:mm"),
    end_date: moment(reservation.end_date).format("YYYY-MM-DDTHH:mm"),
    customer,
  };
  customerInput.value = customer.name || null;
};

const onEditFromDialog = (reservation: Reservation) => {
  eventDialogOpen.value = false;
  editReservation(reservation);
};

const resetEditedReservation = () => {
  editedReservation.value = {
    id: 0,
    start_date: "",
    end_date: "",
    status: ReservationStatus.Draft,
    comment: "",
    services: [],
    users: [],
    employees: [],
    customer: {} as ReservationCustomerInput,
    url: "",
  };
  customerInput.value = null;
  customerSearchResults.value = [];
  customerMenuOpen.value = false;
  applySoleEmployeeDefault();
};

const openCreateSidebar = (dateTime: Temporal.ZonedDateTime) => {
  sidebarOpen.value = true;
  const jsDate = new Date(dateTime.epochMilliseconds);
  editedReservation.value = {
    ...editedReservation.value,
    start_date: moment(jsDate).format("YYYY-MM-DDTHH:00"),
    end_date: moment(jsDate).add(1, "hour").format("YYYY-MM-DDTHH:00"),
  };
  applySoleEmployeeDefault();
};

const openCreateSidebarFromFab = () => {
  resetEditedReservation();
  // Default the new reservation to the next full hour of whichever date the
  // calendar is currently showing, rather than the next hour of "now" -
  // using real "now" regardless of the visible range meant a reservation
  // created while looking at a future day landed outside that range and
  // silently didn't show up until the user navigated back to today.
  const nextHour = Temporal.Now.plainTimeISO().round({
    smallestUnit: "hour",
    roundingMode: "ceil",
  });
  const target = calendarControls
    .getDate()
    .toZonedDateTime({ timeZone: localTimeZone, plainTime: nextHour });
  sidebarOpen.value = true;
  editedReservation.value = {
    ...editedReservation.value,
    start_date: moment(new Date(target.epochMilliseconds)).format(
      "YYYY-MM-DDTHH:00",
    ),
    end_date: moment(new Date(target.epochMilliseconds))
      .add(1, "hour")
      .format("YYYY-MM-DDTHH:00"),
  };
};
</script>

<style lang="scss" scoped>
@use "../styles/_breakpoints" as bp;

.schedules {
  // The reservation form now lives in a VDialog rather than an inline
  // sidebar, so .schedules__content is the only child - it's safe to pin a
  // full height at every breakpoint (previously this was only done at the
  // desktop breakpoint, since pinning it on mobile used to stretch a
  // flex:2 sidebar-less content column and left a tall empty card below the
  // 400px-tall calendar).
  padding: 0 30px;
  display: flex;
  flex-direction: column;
  padding-bottom: 50px;
  height: calc(100% - 126px);

  @include bp.tablet {
    padding: 0 12px;
    padding-bottom: 16px;
    height: calc(100dvh - 142px);
  }

  &__content {
    width: 100%;
    border-radius: 20px;
    background: #fff;
    flex: 1;
    display: flex;
    min-height: 0;
  }

  &__calendar {
    position: relative;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    padding: 25px;
    display: flex;
    flex-direction: column;

    // Schedule-X's default theme reads its colors from global CSS custom
    // properties - swap the primary accent (used for the today marker, the
    // selected view/date, and other highlights) for a shade drawn from the
    // app's own pink-to-purple background gradient (see App.vue) so the
    // calendar reads as part of this app rather than a bolted-on widget.
    --sx-color-primary: #9b5fb3;
    --sx-color-on-primary: #fff;
    --sx-color-primary-container: #f1e4f6;
    --sx-color-on-primary-container: #3d2645;

    // Default theme reserves 75px on the left of the day/week grid for the
    // hour labels (07, 08, ...) - narrower than any of those labels need.
    --sx-calendar-week-grid-padding-left: 40px;

    :deep(.sx-vue-calendar-wrapper) {
      flex: 1;
      min-height: 0;
    }

    // Replaced by .schedules__header + the mini month-picker below, which
    // together cover both the view selector and the date field this built-
    // in toolbar used to show.
    :deep(.sx__calendar-header) {
      display: none;
    }

    // Default theme stacks the weekday name above the date-number circle
    // in its own column - taller than it needs to be, especially in day
    // view where this is the only column and spans the full width. A row
    // instead, name beside the circle, is far more compact.
    :deep(.sx__week-grid__date) {
      flex-direction: row;
      gap: 6px;
      padding: 8px 0;
    }

    // The selected/today day-number badge sizes itself to 2em of its own
    // font-size (--sx-font-extra-large, 1.25rem = ~40px circle) - shrink
    // just that font-size rather than the shared variable, which other
    // elements also read.
    :deep(.sx__week-grid__date-number) {
      font-size: 0.95rem;
    }

    @include bp.tablet {
      padding: 12px;
    }
  }

  &__loading {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 5;
  }

  &__header {
    display: flex;
    justify-content: center;
    padding-bottom: 12px;
    flex: 0 0 auto;
  }

  &__header-toggle {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: none;
    border: none;
    padding: 6px 12px;
    border-radius: 999px;
    font-size: 16px;
    font-weight: 600;
    color: #3d2645;
    cursor: pointer;

    &:hover,
    &:focus-visible {
      background: #f1e4f6;
    }
  }

  // Embedded in the page's own content flow (not a floating popup) - a
  // CSS-grid row transitioning between 0fr and 1fr collapses/reveals it
  // without needing to measure its content's height in JS, and the main
  // calendar right below (flex: 1) simply gets less room as this grows.
  &__date-picker {
    flex: 0 0 auto;
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 0.28s cubic-bezier(0.22, 1, 0.36, 1);

    &--open {
      grid-template-rows: 1fr;
    }
  }

  &__date-picker-inner {
    overflow: hidden;
    min-height: 0;
    margin-bottom: 12px;
    border-radius: 16px;
    border: 1px solid #f1e4f6;

    :deep(.sx-vue-calendar-wrapper),
    :deep(.sx__month-grid-wrapper),
    :deep(.sx__month-grid-week) {
      width: 100%;
      height: auto;
      box-sizing: border-box;
    }

    // No week-number gutter (schedule-x's own default is off, but this
    // stays explicit since it would otherwise eat into the little width
    // 7 columns already have to share in a panel this narrow).
    :deep(.sx__month-grid-week__week-number) {
      display: none;
    }

    :deep(.sx__month-grid-day) {
      flex: 1 1 0;
      min-width: 0;
      box-sizing: border-box;
    }

    // A compact grid with every event collapsed to a single dot (see
    // monthGridOptions.nEventsPerDay: 0 in the script) instead of the
    // default theme's "+N events" text link.
    :deep(.sx__month-grid-day__header) {
      padding: 2px 0;
    }

    :deep(.sx__month-grid-day__header-day-name) {
      font-size: 10px;
    }

    :deep(.sx__month-grid-day__header-date) {
      font-size: 12px;
      height: 1.6em;
      width: 1.6em;
      box-sizing: border-box;
    }

    // The date the main calendar is currently showing - a ring rather than
    // a fill, so it stays visually distinct from the filled "today" badge
    // (.sx__is-today, further below) when they're not the same day.
    :deep(
      .sx__month-grid-day.is-selected
        .sx__month-grid-day__header-date:not(.sx__is-today)
    ) {
      border: 2px solid var(--sx-color-primary);
    }

    :deep(.sx__month-grid-day__events-more) {
      display: block;
      width: 6px;
      height: 6px;
      margin: 2px auto 0;
      padding: 0;
      border: none;
      border-radius: 50%;
      background-color: var(--sx-color-primary);
      font-size: 0;
      line-height: 0;
      overflow: hidden;
    }
  }

  &__fab {
    position: fixed;
    right: 24px;
    bottom: calc(24px + env(safe-area-inset-bottom, 0px));
    z-index: 10;

    @include bp.tablet {
      right: 16px;
    }

    // header.vue's bottom nav bar occupies this same edge below Vuetify's
    // `md` breakpoint (960px, its own `d-md-none` threshold) - clear it
    // instead of sitting underneath it.
    @media screen and (max-width: 959.98px) {
      bottom: calc(96px + env(safe-area-inset-bottom, 0px));
    }
  }
}

.reservation-dialog {
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__title {
    font-size: 18px;
    font-weight: 600;
  }
}
</style>
